/**
 * AI Response Contract Validator
 *
 * Validates AI responses against the contract specification
 * Based on: /docs/ai-service/ai-response-contract-spec.md
 */

import {
  ContractValidationResult,
  TIER_1_ENUMS,
  LayerType,
  LAYER_TYPE_MAP,
} from '../types/ai-response-contract.types';

export class AIContractValidator {
  /**
   * Validate an AI response against the contract
   */
  static validate(response: any, layer?: string): ContractValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // 1. Validate top-level structure
      this.validateTopLevel(response, errors);

      // 2. Validate data structure
      if (response.data) {
        this.validateDataStructure(response.data, errors);

        // 3. Validate layer-specific metadata (handle both layerMetadata and layer-specific fields)
        if (layer) {
          const layerType =
            LAYER_TYPE_MAP[layer] || (layer.toLowerCase() as LayerType);

          // Check for generic layerMetadata first
          let metadataToValidate = response.data.layerMetadata;

          // If not found, check for layer-specific field names
          if (!metadataToValidate) {
            const layerSpecificFields = {
              G: 'songMetadata',
              S: 'starMetadata',
              L: 'looksMetadata',
              M: 'movesMetadata',
              W: 'worldsMetadata',
            } as const;

            const expectedField =
              layerSpecificFields[layer as keyof typeof layerSpecificFields];
            if (expectedField && response.data[expectedField]) {
              metadataToValidate = response.data[expectedField];
              warnings.push(
                `Using layer-specific field '${expectedField}' instead of standard 'layerMetadata'`
              );
            }
          }

          if (metadataToValidate) {
            this.validateLayerMetadata(
              metadataToValidate,
              layerType,
              errors,
              warnings
            );
          } else {
            warnings.push(
              `No metadata found for layer ${layer} (checked layerMetadata and layer-specific fields)`
            );
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
      };
    } catch (error) {
      errors.push(
        `Validation error: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
      return {
        isValid: false,
        errors,
        warnings,
      };
    }
  }

  /**
   * Validate top-level contract fields
   */
  private static validateTopLevel(response: any, errors: string[]): void {
    // Check required fields
    if (typeof response.success !== 'boolean') {
      errors.push('Field "success" must be boolean');
    }

    if (typeof response.data !== 'object' || response.data === null) {
      errors.push('Field "data" must be object');
    }

    if (typeof response.processingMethod !== 'string') {
      errors.push('Field "processingMethod" must be string');
    }

    if (typeof response.confidence !== 'number') {
      errors.push('Field "confidence" must be number');
    } else if (response.confidence < 0 || response.confidence > 1) {
      errors.push('Field "confidence" must be between 0 and 1');
    }

    if (typeof response.processingTimeMs !== 'number') {
      errors.push('Field "processingTimeMs" must be number');
    }

    if (typeof response.fallbackUsed !== 'boolean') {
      errors.push('Field "fallbackUsed" must be boolean');
    }

    // Check optional fields if present
    if (
      response.approach !== undefined &&
      typeof response.approach !== 'string'
    ) {
      errors.push('Field "approach" must be string when present');
    }

    if (response.error !== undefined) {
      if (typeof response.error !== 'object' || response.error === null) {
        errors.push('Field "error" must be object when present');
      } else {
        if (typeof response.error.message !== 'string') {
          errors.push('Field "error.message" must be string');
        }
        if (
          response.error.code !== undefined &&
          typeof response.error.code !== 'string'
        ) {
          errors.push('Field "error.code" must be string when present');
        }
      }
    }
  }

  /**
   * Validate data structure
   */
  private static validateDataStructure(data: any, errors: string[]): void {
    if (typeof data.description !== 'string') {
      errors.push('Field "data.description" must be string');
    }

    if (!Array.isArray(data.tags)) {
      errors.push('Field "data.tags" must be array');
    } else {
      // Validate each tag is a string
      data.tags.forEach((tag: any, index: number) => {
        if (typeof tag !== 'string') {
          errors.push(`Tag at index ${index} must be string`);
        }
      });
    }

    if (typeof data.layerMetadata !== 'object' || data.layerMetadata === null) {
      errors.push('Field "data.layerMetadata" must be object');
    }
  }

  /**
   * Validate layer-specific metadata
   */
  private static validateLayerMetadata(
    layerMetadata: any,
    layerType: LayerType,
    errors: string[],
    warnings: string[]
  ): void {
    const enumMap = TIER_1_ENUMS[layerType];
    if (!enumMap) {
      warnings.push(`No validation rules found for layer type: ${layerType}`);
      return;
    }

    // Validate Tier 1 enum fields
    Object.entries(enumMap).forEach(([fieldName, allowedValues]) => {
      const fieldValue = layerMetadata[fieldName];

      if (fieldValue !== undefined && fieldValue !== null) {
        if (!allowedValues.includes(fieldValue as any)) {
          errors.push(
            `Layer metadata field "${fieldName}" has invalid value "${fieldValue}". ` +
              `Allowed values: ${allowedValues.join(', ')}`
          );
        }
      }
    });

    // Layer-specific required field validation
    this.validateRequiredFields(layerMetadata, layerType, errors);
  }

  /**
   * Validate required fields for each layer
   */
  private static validateRequiredFields(
    layerMetadata: any,
    layerType: LayerType,
    errors: string[]
  ): void {
    switch (layerType) {
      case 'songs':
        this.validateRequiredStringFields(
          layerMetadata,
          [
            'songTitle',
            'artistName',
            'energy',
            'vocalType',
            'tempo',
            'danceability',
            'originalArtistGender',
            'genderSuitability',
            'ageAppropriateness',
            'genre',
            'mood',
          ],
          errors
        );
        break;

      case 'stars':
        this.validateRequiredStringFields(
          layerMetadata,
          [
            'archetype',
            'energyLevel',
            'gender',
            'ageGroup',
            'hairLength',
            'hairStyle',
            'makeupType',
            'colorPalette',
            'starName',
          ],
          errors
        );
        break;

      case 'looks':
        this.validateRequiredStringFields(
          layerMetadata,
          ['occasion', 'styleCategory', 'season', 'gender', 'ageGroup'],
          errors
        );
        this.validateRequiredArrayFields(
          layerMetadata,
          ['brandNames', 'colors'],
          errors
        );
        break;

      case 'moves':
        this.validateRequiredStringFields(
          layerMetadata,
          ['energyLevel', 'complexity'],
          errors
        );
        break;

      case 'worlds':
        this.validateRequiredStringFields(layerMetadata, ['timeOfDay'], errors);
        break;
    }
  }

  /**
   * Validate required string fields
   */
  private static validateRequiredStringFields(
    obj: any,
    fields: string[],
    errors: string[]
  ): void {
    fields.forEach(fieldName => {
      if (!obj[fieldName] || typeof obj[fieldName] !== 'string') {
        errors.push(`Required field "${fieldName}" must be a non-empty string`);
      }
    });
  }

  /**
   * Validate required array fields
   */
  private static validateRequiredArrayFields(
    obj: any,
    fields: string[],
    errors: string[]
  ): void {
    fields.forEach(fieldName => {
      if (!Array.isArray(obj[fieldName])) {
        errors.push(`Required field "${fieldName}" must be an array`);
      }
    });
  }

  /**
   * Quick validation helper for common use cases
   */
  static isValidResponse(response: any, layer?: string): boolean {
    const result = this.validate(response, layer);
    return result.isValid;
  }

  /**
   * Get validation errors as formatted string
   */
  static getValidationErrors(response: any, layer?: string): string {
    const result = this.validate(response, layer);
    if (result.isValid) return '';

    return result.errors.join('\n');
  }

  /**
   * Log validation results to console (for debugging)
   */
  static logValidation(
    response: any,
    layer?: string,
    label = 'AI Response'
  ): void {
    const result = this.validate(response, layer);

    if (result.isValid) {
      console.log(`✅ ${label} - Contract validation passed`);
      if (result.warnings.length > 0) {
        console.warn(`⚠️ ${label} - Warnings:`, result.warnings);
      }
    } else {
      console.error(`❌ ${label} - Contract validation failed:`);
      result.errors.forEach(error => console.error(`  - ${error}`));
      if (result.warnings.length > 0) {
        console.warn(`⚠️ ${label} - Warnings:`, result.warnings);
      }
    }
  }
}
